import { useEffect, useState } from 'react';
import {
  CORROBORATION_WINDOW_HOURS,
  corroborationBbox,
  corroborationLabel,
} from '../lib/corroboration';
import { focusNewsTab } from '../newsFocus';

/**
 * Incident corroboration chip v1 (#183) — a contextual "N social reports
 * nearby" line on a tracked incident's detail panel. The count is the
 * geo-bearing media_signals inside a bbox around the incident centroid in the
 * wire's recent window (dismissed signals already excluded server-side).
 *
 * PART IX contextual-only rule (the #185 worked example): renders NOTHING when
 * N = 0 — no empty state, no "0 reports" line. Clicking opens the News &
 * Social surface (rail tab here + the panel tab if one is open) filtered to
 * the same bbox. Count, never content, on the incident panel — the wire stays
 * ambient and never scores anything (no auto-urgency, #183 precision guard).
 */
export function IncidentCorroborationChip({
  lon,
  lat,
  incidentNumber,
}: {
  lon: number;
  lat: number;
  incidentNumber: string;
}): JSX.Element | null {
  const [count, setCount] = useState(0);
  const bbox = corroborationBbox(lon, lat);

  useEffect(() => {
    let cancelled = false;
    const ctl = new AbortController();
    const { west, south, east, north } = corroborationBbox(lon, lat);
    void fetch(
      `/api/media-signals?bbox=${west},${south},${east},${north}&hours=${CORROBORATION_WINDOW_HOURS}&limit=500`,
      { signal: ctl.signal, cache: 'no-store' },
    )
      .then((r) => (r.ok ? (r.json() as Promise<{ signals: unknown[] }>) : null))
      .then((body) => {
        if (!cancelled && body) setCount(body.signals.length);
      })
      .catch(() => {
        /* contextual chrome — a failed count just stays hidden. */
      });
    return () => {
      cancelled = true;
      ctl.abort();
    };
  }, [lon, lat]);

  const label = corroborationLabel(count);
  if (!label) return null;

  return (
    <button
      type="button"
      data-testid="incident-corroboration-chip"
      onClick={() =>
        focusNewsTab({
          west: bbox.west,
          south: bbox.south,
          east: bbox.east,
          north: bbox.north,
          label: incidentNumber,
        })
      }
      title="Open the News & Social feed filtered to reports near this incident"
      className="mt-1.5 flex items-center gap-1.5 rounded border border-slate-600/80 px-2 py-1 text-[11px] text-slate-300 hover:border-slate-400 hover:text-slate-100"
    >
      <span aria-hidden>🗞</span>
      <span>{label}</span>
    </button>
  );
}
